/**
 * markerInfluenceMap.js — iter882 (Feb 2026)
 *
 * Static map: canonical marker_code → list of BHI / Digital-Twin / KEGG modules
 * that consume this marker via `PatientHealthContext` + twin overlays.
 *
 * SOURCE OF TRUTH (verified against code):
 *   • services/patient_health_context.py:571-589   → _unified_to_ctx mapping
 *   • routes/metabolic_insulin_twin.py             → HbA1c, insulin, glucose, HOMA-IR
 *   • routes/cardio_twin.py                        → LDL, HDL, TG, hsCRP
 *   • routes/liver_kidney_twin.py                  → creatinine, GGT, ALT, AST
 *   • routes/health_twin.py                        → generic overlay
 *   • routes/breast_intelligence.py                → DUTCH metabolites + vit D + omega-3
 *   • services/condition_pathway_map.py            → KEGG pathway consumers
 *   • services/nutrigenomics.py                    → DNA variants
 *
 * Kept in FRONTEND only — no runtime cost, no API call. If you add a new
 * consumer downstream, add the module here too.
 */

// Module labels for chip display (short, Bulgarian).
export const MODULE_META = {
  bhi:          { label_bg: "BHI",              color: "pink" },
  bdi:          { label_bg: "BDI (18 карти)",   color: "fuchsia" },
  btre:         { label_bg: "BTRE",             color: "purple" },
  bpp:          { label_bg: "BPP",              color: "violet" },
  metabolic:    { label_bg: "Metabolic Twin",   color: "amber" },
  cardio:       { label_bg: "Cardio Twin",      color: "rose" },
  inflammation: { label_bg: "Inflammation Twin",color: "orange" },
  hormonal:     { label_bg: "Hormonal Twin",    color: "sky" },
  thyroid:      { label_bg: "Thyroid Twin",     color: "cyan" },
  liver_kidney: { label_bg: "Liver-Kidney Twin",color: "emerald" },
  cognitive:    { label_bg: "Cognitive Twin",   color: "indigo" },
  hematology:   { label_bg: "Hematology",       color: "red" },
  nutrigenomics:{ label_bg: "Nutrigenomics",    color: "teal" },
  estrogen_dom: { label_bg: "Estrogen Dominance", color: "pink" },
  methylation:  { label_bg: "Methylation Cycle",color: "blue" },
  kegg:         { label_bg: "KEGG пътища",      color: "purple" },
};

// canonical marker_code → modules[]
export const MARKER_INFLUENCE_MAP = {
  // ─── Glucose / insulin axis ───────────────────────────────
  hba1c:              ["metabolic", "bhi", "bdi"],
  fasting_glucose:    ["metabolic", "bhi", "bdi"],
  fasting_insulin:    ["metabolic", "bhi", "bdi", "kegg"],
  homa_ir:            ["metabolic", "bhi", "bdi"],
  // ─── Lipids ────────────────────────────────────────────────
  ldl_cholesterol:    ["cardio", "metabolic"],
  hdl_cholesterol:    ["cardio", "metabolic"],
  triglycerides:      ["cardio", "metabolic"],
  total_cholesterol:  ["cardio"],
  tg_hdl_ratio:       ["cardio", "metabolic"],
  // ─── Inflammation ─────────────────────────────────────────
  hs_crp:             ["inflammation", "cardio", "bhi", "bdi", "btre", "kegg"],
  esr:                ["inflammation"],
  // ─── Vitamins / minerals ──────────────────────────────────
  vitamin_d_25oh:     ["bhi", "btre", "bdi", "kegg"],
  vitamin_b12:        ["cognitive", "methylation", "kegg"],
  folate:             ["methylation", "kegg"],
  ferritin:           ["hematology", "liver_kidney"],
  omega3_index:       ["inflammation", "bhi", "btre", "bdi"],
  homocysteine:       ["methylation", "cardio", "cognitive"],
  creatinine:         ["liver_kidney"],
  // ─── Hematology (CBC) ─────────────────────────────────────
  hemoglobin:         ["hematology"],
  hematocrit:         ["hematology"],
  wbc:                ["hematology", "inflammation"],
  rbc:                ["hematology"],
  platelets:          ["hematology"],
  // ─── Thyroid ──────────────────────────────────────────────
  tsh:                ["thyroid", "hormonal", "bhi"],
  ft3:                ["thyroid", "hormonal"],
  ft4:                ["thyroid", "hormonal"],
  anti_tpo:           ["thyroid", "inflammation"],
  anti_tg:            ["thyroid", "inflammation"],
  // ─── Sex hormones (serum) ─────────────────────────────────
  estradiol:          ["hormonal", "bhi", "bdi", "estrogen_dom"],
  progesterone:       ["hormonal", "bhi", "bdi"],
  lh:                 ["hormonal"],
  fsh:                ["hormonal"],
  amh:                ["hormonal"],
  prolactin:          ["hormonal", "bdi"],
  shbg:               ["hormonal", "metabolic"],
  testosterone:       ["hormonal"],
  dhea_s:             ["hormonal"],
  // ─── DUTCH metabolites (urine) ────────────────────────────
  dutch_2_oh_e1:      ["bhi", "estrogen_dom", "bdi", "kegg"],
  dutch_16_oh_e1:     ["bhi", "estrogen_dom", "bdi", "kegg"],
  dutch_4_oh_e1:      ["bhi", "estrogen_dom", "bdi", "kegg"],
  dutch_2_meo_e1:     ["bhi", "estrogen_dom", "bdi", "kegg"],
  dutch_e2_urine:     ["bhi", "estrogen_dom"],
  dutch_e1_urine:     ["bhi", "estrogen_dom"],
  dutch_e3_urine:     ["bhi", "estrogen_dom"],
  dutch_dhea_s:       ["hormonal", "bhi"],
  dutch_testosterone: ["hormonal", "bhi"],
  dutch_cortisol:     ["hormonal", "bhi"],
  dutch_melatonin:    ["bhi", "kegg"],
  // ─── DNA variants ─────────────────────────────────────────
  dna_mthfr_c677t:    ["nutrigenomics", "methylation", "bdi", "kegg"],
  dna_mthfr_a1298c:   ["nutrigenomics", "methylation", "kegg"],
  dna_comt_val158met: ["nutrigenomics", "methylation", "estrogen_dom", "bdi", "kegg"],
  dna_brca1:          ["nutrigenomics", "bdi", "btre", "kegg"],
  dna_brca2:          ["nutrigenomics", "bdi", "btre", "kegg"],
  dna_gstm1:          ["nutrigenomics", "kegg"],
  dna_gstt1:          ["nutrigenomics", "kegg"],
  dna_cyp1a1_m1:      ["nutrigenomics", "estrogen_dom", "kegg"],
  dna_cyp1b1_l432v:   ["nutrigenomics", "estrogen_dom", "kegg"],
  dna_vdr_bsmi:       ["nutrigenomics", "kegg"],
  dna_vdr_taqi:       ["nutrigenomics", "kegg"],
  dna_vdr_foki:       ["nutrigenomics", "kegg"],
  dna_bcmo1:          ["nutrigenomics"],
  dna_fto_rs9939609:  ["nutrigenomics", "metabolic"],
  dna_apoe:           ["nutrigenomics", "cardio", "cognitive"],
  dna_tcf7l2:         ["nutrigenomics", "metabolic"],
};

/**
 * Get the influence chips for a given marker code.
 * @param {string} code — canonical marker_code (e.g. "hba1c")
 * @returns {Array<{key:string, label_bg:string, color:string}>}
 */
export function influencesFor(code) {
  const keys = MARKER_INFLUENCE_MAP[code] || [];
  return keys.map((k) => ({ key: k, ...MODULE_META[k] })).filter((x) => x.label_bg);
}
